// --- React & Redux
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import { Row } from 'antd';
import Select from '@dtci/spellbook/Select';
// --- API
import { datastageApi } from 'datastage/api';
// --- Hooks & Store
import { useDealVersionActions } from 'datastage/store/dealVersion/useDealVersion';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel/InputLabel';
// --- Styles
import '../styles.scss';

/**
 * Deal Status Form
 *
 * Features:
 * - Renders a form for selecting deal status and deal

 * @returns {Element}
 * @constructor
 */
export function DealStatusForm() {
    const timeoutRef = useRef();
    const debounceTimeout = 500;
    const {
        statusOptions,
        selectedStatus,
        selectedDeal,
    } = useSelector((state) => state.dealVersion);
    const { updateDealVersion } = useDealVersionActions();

    // Handle loading deal options based on user input with debounce
    const onDealOptionChange = (input, loadOptions) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(async () => {
            if (input) {
                const data = await datastageApi.deals(input);
                loadOptions(
                    data.map((item) => ({
                        id: item?.id,
                        label: item?.searchableTitle,
                        template: item?.template?.templateName,
                    })),
                );
            }
        }, debounceTimeout);
    };

    const handleInputChange = (field) => (value) => {
        updateDealVersion({
            [field]: value,
        });
    };

    return (
        <div className="form-section">
            <div className="form-section-title">Update Deal Status</div>
            <div className="form-section-content">
                <Row>
                    <InputLabel colProps={{ span: 24 }} label="Status" required>
                        <Select
                            isSearchable
                            options={statusOptions}
                            onChange={handleInputChange('selectedStatus')}
                            value={selectedStatus || statusOptions[0]}
                        />
                    </InputLabel>
                </Row>
                <Row>
                    <InputLabel
                        colProps={{ span: 24 }}
                        label="Deal - Search by ID or Title"
                        required
                        error={!selectedDeal && 'Deal is mandatory and cannot be blank'}
                    >
                        <Select
                            async
                            className="search-input"
                            loadOptions={onDealOptionChange}
                            isClearable
                            isSearchable
                            onChange={handleInputChange('selectedDeal')}
                            value={selectedDeal}
                        />
                    </InputLabel>
                </Row>
            </div>
        </div>
    );
}
